import { Injectable } from '@angular/core';
import { ApplicationInsights, SeverityLevel, DistributedTracingModes } from '@microsoft/applicationinsights-web';
import { AppConfigService } from './app-config.service';
import { AngularPlugin } from '@microsoft/applicationinsights-angularplugin-js';
import { Router } from '@angular/router';
import { ErrorHandlerService } from './error-handler.service';

@Injectable({
  providedIn: 'root',
})
export class AppInsightsService {
  private appInsights: ApplicationInsights | undefined;
  private configuredSeverityLevel: number = SeverityLevel.Information;

  constructor(private readonly appConfigService: AppConfigService,
    private readonly router: Router) { }

  async init() {
    if (this.appInsights) return;
    const angularPlugin = new AngularPlugin();
    const gateway = new URL(this.appConfigService.config?.apiConfig?.basePath || '/', window.location.origin);
    this.appInsights = new ApplicationInsights({
      config: {
        connectionString: this.appConfigService.config?.connectionString,
        appId: this.appConfigService.config?.connectionString?.match(/(?:^|;)ApplicationId=([^;]+)/i)?.[1],
        // AngularPlugin owns route tracking; do not also enable the SDK route hook.
        enableAutoRouteTracking: false,
        enableCorsCorrelation: true,
        distributedTracingMode: DistributedTracingModes.W3C,
        correlationHeaderDomains: [gateway.host],
        addRequestContext: details => {
          const responseUrl = details?.xhr?.responseURL || details?.request?.url;
          if (!responseUrl) return {};
          const target = new URL(responseUrl, window.location.origin);
          const prefix = gateway.pathname.replace(/\/$/, '');
          if (target.origin !== gateway.origin || !(target.pathname === prefix || target.pathname.startsWith(`${prefix}/`))) return {};
          return {
            ActionId: details?.xhr?.getResponseHeader('X-UI-Action-Id') || undefined,
            ServerTraceId: details?.xhr?.getResponseHeader('X-Trace-Id') || undefined
          };
        },
        enableResponseHeaderTracking: true,
        extensions: [<any>angularPlugin],
        extensionConfig: {
          [angularPlugin.identifier]: { router: this.router, errorServices: [new ErrorHandlerService(this)] }
        }
      },
    });
    this.appInsights.loadAppInsights();
    this.appInsights.addTelemetryInitializer((envelope) => {
      envelope.tags = envelope.tags || [];
      envelope.tags['ai.cloud.role'] = 'GenesysCommUI';
    });
    this.configuredSeverityLevel = this.getConfiguredSeverity();
    this.appInsights.trackPageView();
  }

  setUser(tenantId?: string, objectId?: string) {
    if (tenantId && objectId) {
      this.appInsights?.setAuthenticatedUserContext(`${tenantId}.${objectId}`);
    } else {
      this.appInsights?.clearAuthenticatedUserContext();
    }
  }

  getSessionId(): string | undefined {
    return this.appInsights?.context.getSessionId();
  }

   private getConfiguredSeverity(): number {
    const levelStr = this.appConfigService.config?.LogLevel;
    return SeverityLevel[levelStr as keyof typeof SeverityLevel] ?? SeverityLevel.Information;
  }

  logPageView(name?: string, url?: string) {
    this.appInsights?.trackPageView({
      name: name,
      uri: url,
    });
  }

  // Scope only the synchronous send/track call. Never hold shared SDK context across
  // an await: parallel requests must capture independent W3C traces.
  withAction<T>(id: string, name: string, work: () => T): T {
    const trace = this.appInsights?.core?.getTraceCtx();
    if (!trace) return work();
    const previous = { id: trace.getTraceId(), span: trace.getSpanId(), name: trace.getName() };
    trace.setTraceId(id);
    trace.setSpanId(id.slice(0, 16));
    trace.setName(name);
    try {
      return work();
    } finally {
      trace.setTraceId(previous.id);
      trace.setSpanId(previous.span);
      trace.setName(previous.name);
    }
  }

  // Action breadcrumbs are required for debugging even when verbose UI logs are disabled.
  logAction(name: string, properties: { [key: string]: unknown }) {
    this.withAction(String(properties['ActionId']), String(properties['ActionName']), () =>
      this.appInsights?.trackEvent({ name }, properties));
  }

  logEvent(name: string, properties?: { [key: string]: any }, severityLevel: number = SeverityLevel.Information) {
    if (severityLevel >= this.configuredSeverityLevel) {
    this.appInsights?.trackEvent({ name: name }, properties);
    }
  }

  logException(exception: Error, severityLevel: number = SeverityLevel.Error) {
    if (severityLevel >= this.configuredSeverityLevel) {
      this.appInsights?.trackException({ exception: exception, severityLevel: severityLevel });
    }
  }

  logTrace(message: string, properties?: { [key: string]: any }, severityLevel: number = SeverityLevel.Information) {
    if (severityLevel >= this.configuredSeverityLevel) {
      this.appInsights?.trackTrace({ message: message, severityLevel: severityLevel }, properties);
    }
  }
}
